// Shared pieces for the Ledger screens: elements, money, ranges, charts, sheets.

export type Range = '1m' | '3m' | '1y' | 'all';
export interface SeriesPoint { date: string; value: number }

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const cents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** $1,234 — a minus sign for negatives, no cents. */
export function money(n: number): string {
  const s = whole.format(Math.abs(n));
  return Math.round(n) < 0 ? '−' + s : s;
}

export function moneyCents(n: number): string {
  return cents.format(Math.abs(n));
}

/** +$412 / −$85 */
export function signed(n: number): string {
  if (Math.round(n) === 0) return '$0';
  return (n > 0 ? '+' : '−') + whole.format(Math.abs(n));
}

export function isDebt(type: string): boolean {
  return type === 'credit_card' || type === 'loan';
}

/** An arrow and a signed amount, green for up and red for down. Up means the
 *  balance rose: on a card that is the debt shrinking. */
export function changeLine(change: number, suffix: string): HTMLElement {
  const dir = Math.round(change) > 0 ? 'up' : Math.round(change) < 0 ? 'down' : 'muted';
  const line = h('div', 'change');
  const amt = h('span', dir);
  amt.append(h('span', '', dir === 'up' ? '↑ ' : dir === 'down' ? '↓ ' : ''), h('span', 'money', signed(change)));
  line.append(amt, h('span', 'muted', suffix));
  return line;
}

/** An account's change over the range. A debt reads in what is OWED: "↓ $412"
 *  in green means you owe $412 less. Everything else reads like net. */
export function accountChange(type: string, change: number): HTMLElement {
  const r = Math.round(change);
  const good = r > 0 ? 'up' : r < 0 ? 'down' : 'muted';
  if (!isDebt(type)) {
    return h('div', `acct-chg ${good}`, `${r > 0 ? '↑ ' : r < 0 ? '↓ ' : ''}${signed(change)}`);
  }
  const arrow = r > 0 ? '↓ ' : r < 0 ? '↑ ' : '';
  return h('div', `acct-chg ${good}`, `${arrow}${whole.format(Math.abs(change))} ${r > 0 ? 'less' : r < 0 ? 'more' : ''}`.trim());
}

// ── Range ─────────────────────────────────────────────────────────────

const RANGE_KEY = 'ch-range';
export const RANGES: { id: Range; label: string; long: string }[] = [
  { id: '1m', label: '1M', long: 'past month' },
  { id: '3m', label: '3M', long: 'past 3 months' },
  { id: '1y', label: '1Y', long: 'past year' },
  { id: 'all', label: 'All', long: 'all time' },
];

export function getRange(): Range {
  try {
    const r = localStorage.getItem(RANGE_KEY);
    if (r === '1m' || r === '3m' || r === '1y' || r === 'all') return r;
  } catch { /* storage blocked */ }
  return '3m';
}

export function setRange(r: Range): void {
  try { localStorage.setItem(RANGE_KEY, r); } catch { /* storage blocked */ }
}

export function rangeLong(r: Range): string {
  return RANGES.find(x => x.id === r)!.long;
}

export function rangeSwitch(current: Range, onPick: (r: Range) => void): HTMLElement {
  const wrap = h('div', 'ranges');
  for (const r of RANGES) {
    const b = h('button', r.id === current ? 'on' : '', r.label);
    b.addEventListener('click', () => { if (r.id !== current) { setRange(r.id); onPick(r.id); } });
    wrap.appendChild(b);
  }
  return wrap;
}

// ── Charts ────────────────────────────────────────────────────────────

const SVG = 'http://www.w3.org/2000/svg';

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function trendColor(series: SeriesPoint[]): string {
  if (series.length < 2) return 'var(--muted)';
  const d = series[series.length - 1].value - series[0].value;
  return Math.round(d) < 0 ? 'var(--down)' : 'var(--up)';
}

/** A small trend line for an account row. Fixed box, stretched to fit. */
export function sparkline(series: SeriesPoint[]): SVGSVGElement {
  const w = 88, hgt = 30;
  const svg = svgEl('svg', { viewBox: `0 0 ${w} ${hgt}`, width: w, height: hgt, preserveAspectRatio: 'none', 'aria-hidden': 'true' });
  if (series.length < 2) return svg;
  const vals = series.map(p => p.value);
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${hgt - 2 - ((v - min) / span) * (hgt - 4)}`).join(' ');
  svg.appendChild(svgEl('polyline', {
    points: pts, fill: 'none', stroke: trendColor(series), 'stroke-width': 1.5,
    'vector-effect': 'non-scaling-stroke', 'stroke-linejoin': 'round',
  }));
  return svg;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' -> 'Aug 2027' */
export function monthName(month: string): string {
  return `${MONTHS[parseInt(month.slice(5, 7), 10) - 1]} ${month.slice(0, 4)}`;
}

/** $1.2k / $850 — for compact lines. */
export function compact(n: number): string {
  const a = Math.abs(n);
  const s = a >= 1000 ? `$${(a / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(a)}`;
  return Math.round(n) < 0 ? '−' + s : s;
}

export function shortDate(date: string, withYear = false): string {
  const m = MONTHS[parseInt(date.slice(5, 7), 10) - 1];
  const d = parseInt(date.slice(8, 10), 10);
  return withYear ? `${m} ${date.slice(0, 4)}` : `${m} ${d}`;
}

function axisMoney(n: number): string {
  const a = Math.abs(n);
  const s = a >= 10000 ? `$${Math.round(a / 1000)}k` : a >= 1000 ? `$${(a / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(a)}`;
  return n < 0 ? '−' + s : s;
}

/** A line chart drawn at the host's real width (so text and the end dot stay
 *  round). Redraws when the host resizes. `axes` adds value and date labels. */
export function lineChart(series: SeriesPoint[], opts: { height: number; axes?: boolean; long?: boolean }): HTMLElement {
  const host = h('div');
  host.style.height = opts.height + 'px';
  const draw = () => {
    const width = host.clientWidth;
    if (!width) return;
    host.innerHTML = '';
    const svg = svgEl('svg', { class: 'chart', width, height: opts.height, viewBox: `0 0 ${width} ${opts.height}` });
    host.appendChild(svg);
    if (series.length < 2) return;
    const vals = series.map(p => p.value);
    let min = Math.min(...vals), max = Math.max(...vals);
    if (max - min < 1) { min -= 50; max += 50; }
    const padL = opts.axes ? 52 : 4, padR = 8, padT = 8, padB = opts.axes ? 24 : 8;
    const cw = width - padL - padR, ch = opts.height - padT - padB;
    const x = (i: number) => padL + (i / (vals.length - 1)) * cw;
    const y = (v: number) => padT + (1 - (v - min) / (max - min)) * ch;
    const color = trendColor(series);

    if (opts.axes) {
      for (const v of [max, (max + min) / 2, min]) {
        svg.appendChild(svgEl('text', { x: padL - 8, y: y(v) + 4, 'text-anchor': 'end', class: 'chart-axis' })).textContent = axisMoney(v);
      }
      svg.appendChild(svgEl('line', { x1: padL, x2: padL, y1: padT, y2: padT + ch, stroke: 'var(--line)' }));
      svg.appendChild(svgEl('line', { x1: padL, x2: padL + cw, y1: padT + ch, y2: padT + ch, stroke: 'var(--line)' }));
      const idx = [0, Math.floor((vals.length - 1) / 2), vals.length - 1];
      idx.forEach((i, k) => {
        const anchor = k === 0 ? 'start' : k === 2 ? 'end' : 'middle';
        svg.appendChild(svgEl('text', { x: x(i), y: opts.height - 6, 'text-anchor': anchor, class: 'chart-axis' }))
          .textContent = shortDate(series[i].date, opts.long);
      });
    }

    svg.appendChild(svgEl('polyline', {
      points: vals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' '),
      fill: 'none', stroke: color, 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round',
    }));
    svg.appendChild(svgEl('circle', { cx: x(vals.length - 1), cy: y(vals[vals.length - 1]), r: 4, fill: color }));
  };
  observeWidth(host, draw);
  return host;
}

/** Draw once the host has a width, and again whenever it changes. Errors are logged
 *  rather than swallowed by the observer. */
function observeWidth(host: HTMLElement, draw: () => void): void {
  const safe = () => { try { draw(); } catch (err) { console.error('chart draw failed', err); } };
  new ResizeObserver(safe).observe(host);
  requestAnimationFrame(safe);
}

// ── Sheet ─────────────────────────────────────────────────────────────

/** A bottom sheet. Returns a close function. Tapping the backdrop closes it. */
export function openSheet(build: (body: HTMLElement, close: () => void) => void): () => void {
  const bg = h('div', 'sheet-bg');
  const sheet = h('div', 'sheet');
  bg.appendChild(sheet);
  const close = () => bg.remove();
  bg.addEventListener('click', (e) => { if (e.target === bg) close(); });
  build(sheet, close);
  document.body.appendChild(bg);
  return close;
}

export function ago(ms: number | null): string {
  if (ms == null) return 'never';
  const s = (Date.now() - ms) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}

export const ICON_GEAR = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
export const ICON_BACK = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="20" y1="12" x2="4" y2="12"/><polyline points="10 6 4 12 10 18"/></svg>';

export function backButton(): HTMLButtonElement {
  const b = h('button', 'icon-btn back-btn');
  b.setAttribute('aria-label', 'Back');
  b.innerHTML = ICON_BACK;
  b.addEventListener('click', () => { location.hash = '#home'; });
  return b;
}

// ── Structure shared by every screen ──────────────────────────────────

/** A titled block: small-caps heading, optional action on the right ("Budget ›"),
 *  then the body. Every screen is a stack of these, so they read the same. */
export function section(title: string, action?: { label: string; onClick: () => void }): { el: HTMLElement; body: HTMLElement } {
  const el = h('section', 'section');
  const head = h('div', 'section-head');
  head.appendChild(h('div', 'eyebrow', title));
  if (action) {
    const a = h('button', 'head-action', `${action.label} ›`);
    a.addEventListener('click', action.onClick);
    head.appendChild(a);
  }
  const body = h('div');
  el.append(head, body);
  return { el, body };
}

/** Two figures side by side: "Spent this month | By Sep 27". */
export function pair(cells: { label: string; value: string; cls?: string }[]): HTMLElement {
  const wrap = h('div', 'pair');
  for (const c of cells) {
    const cell = h('div');
    cell.append(h('div', 'label', c.label), h('div', `money ${c.cls ?? ''}`.trim(), c.value));
    wrap.appendChild(cell);
  }
  return wrap;
}

/** Spent against the target: a bar filled to what's spent, a tick where the month
 *  is, and one line saying what that means. */
export function targetBar(spent: number, target: number, day: number, days: number): HTMLElement {
  const wrap = h('div', 'target');
  const expected = target * day / days;
  const over = spent > target;
  const ahead = spent > expected;
  const bar = h('div', 'target-bar');
  const fill = h('i', ahead ? 'over' : '');
  fill.style.width = `${Math.min(100, (spent / target) * 100)}%`;
  const tick = h('b');
  tick.style.left = `${(day / days) * 100}%`;
  bar.append(fill, tick);
  const left = target - spent;
  const daysLeft = days - day;
  const text = over
    ? `Over the ${money(target)} target by ${money(spent - target)}.`
    : `${money(left)} left of ${money(target)}${daysLeft > 0 ? ` · ${money(left / daysLeft)} a day for ${daysLeft} days` : ''}.`
      + (ahead ? ` ${money(spent - expected)} ahead of pace.` : '');
  wrap.append(bar, h('div', over || ahead ? 'note down' : 'note', text));
  return wrap;
}

/** Cumulative spending through the month: this month (solid), last month (grey),
 *  and the target pace (dashed, $0 on day 1 to the target on the last day). */
export function paceChart(thisMonth: number[], lastMonth: number[], days: number, target: number | null): HTMLElement {
  const host = h('div');
  const height = 200;
  host.style.height = height + 'px';
  const draw = () => {
    const width = host.clientWidth;
    if (!width) return;
    host.innerHTML = '';
    const svg = svgEl('svg', { class: 'chart', width, height, viewBox: `0 0 ${width} ${height}` });
    host.appendChild(svg);
    const max = Math.max(1, target ?? 0, ...thisMonth, ...lastMonth) * 1.05;
    const padL = 48, padR = 8, padT = 8, padB = 22;
    const cw = width - padL - padR, ch = height - padT - padB;
    const x = (day: number) => padL + ((day - 1) / Math.max(1, days - 1)) * cw;
    const y = (v: number) => padT + (1 - v / max) * ch;
    for (const v of [max / 1.05, max / 2.1, 0]) {
      svg.appendChild(svgEl('text', { x: padL - 8, y: y(v) + 4, 'text-anchor': 'end', class: 'chart-axis' })).textContent = axisMoney(v);
      svg.appendChild(svgEl('line', { x1: padL, x2: padL + cw, y1: y(v), y2: y(v), stroke: 'var(--line)' }));
    }
    for (const [d, anchor] of [[1, 'start'], [Math.ceil(days / 2), 'middle'], [days, 'end']] as [number, string][]) {
      svg.appendChild(svgEl('text', { x: x(d), y: height - 6, 'text-anchor': anchor, class: 'chart-axis' })).textContent = String(d);
    }
    const line = (vals: number[], attrs: Record<string, string | number>) => {
      if (vals.length < 1) return;
      const pts = vals.map((v, i) => `${x(i + 1).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
      svg.appendChild(svgEl('polyline', { points: vals.length === 1 ? `${pts} ${pts}` : pts, fill: 'none', 'stroke-linejoin': 'round', 'stroke-linecap': 'round', ...attrs }));
    };
    if (target) svg.appendChild(svgEl('line', { x1: x(1), y1: y(0), x2: x(days), y2: y(target), stroke: 'var(--muted)', 'stroke-width': 1.5, 'stroke-dasharray': '4 4' }));
    line(lastMonth.slice(0, days), { stroke: 'var(--faint)', 'stroke-width': 2 });
    const now = thisMonth[thisMonth.length - 1] ?? 0;
    const behind = target ? now > target * thisMonth.length / days : false;
    const color = behind ? 'var(--down)' : 'var(--up)';
    line(thisMonth, { stroke: color, 'stroke-width': 2.5 });
    if (thisMonth.length) svg.appendChild(svgEl('circle', { cx: x(thisMonth.length), cy: y(now), r: 4, fill: color }));
  };
  observeWidth(host, draw);
  return host;
}

/** Monthly totals as bars, with the target as a line across them. Tapping a bar
 *  calls onPick so the screen can say what that month was. */
export function monthBars(months: { month: string; total: number }[], target: number | null, onPick: (m: { month: string; total: number }) => void): HTMLElement {
  const wrap = h('div', 'mbars');
  const max = Math.max(1, target ?? 0, ...months.map(m => m.total)) * 1.08;
  const bars = h('div', 'bars');
  months.forEach((m, i) => {
    const bar = h('button', i === months.length - 1 ? 'bar now' : 'bar');
    const fill = h('i', target && m.total > target ? 'over' : '');
    fill.style.height = `${(m.total / max) * 100}%`;
    const track = h('div', 'track');
    track.appendChild(fill);
    bar.append(track, h('span', '', MONTHS[parseInt(m.month.slice(5, 7), 10) - 1].charAt(0)));
    bar.addEventListener('click', () => {
      bars.querySelectorAll('.bar').forEach(b => b.classList.remove('picked'));
      bar.classList.add('picked');
      onPick(m);
    });
    bars.appendChild(bar);
  });
  wrap.appendChild(bars);
  if (target) {
    const t = h('div', 'mbars-target');
    t.style.bottom = `${18 + (target / max) * 72}px`;
    wrap.appendChild(t);
  }
  return wrap;
}
